import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { z } from "zod";

// Decode a Supabase JWT and return the auth user id (sub) if the caller
// authenticated. Returns null when there is no bearer token or it is invalid.
async function getOptionalAuthUserId(): Promise<string | null> {
  try {
    const req = getRequest();
    const auth = req?.headers?.get("authorization");
    if (!auth?.startsWith("Bearer ")) return null;
    const token = auth.slice(7).trim();
    if (!token) return null;
    const { data } = await supabaseAdmin.auth.getUser(token);
    return data?.user?.id ?? null;
  } catch {
    return null;
  }
}

async function isOwnerOrAdminOfVendor(
  authUserId: string,
  vendorProfileId: string | null,
): Promise<boolean> {
  const { data: profile } = await supabaseAdmin
    .from("profiles")
    .select("id")
    .eq("auth_user_id", authUserId)
    .maybeSingle();
  if (!profile) return false;
  if (vendorProfileId && profile.id === vendorProfileId) return true;
  const { data: roles } = await supabaseAdmin
    .from("user_roles")
    .select("role")
    .eq("profile_id", profile.id);
  return (roles ?? []).some((r) => r.role === "admin");
}


// Fetch active vendor IDs for filtering published listings.
// A vendor is considered "active" only when their vendor status is active
// AND they have an active subscription (status in active/trialing).
async function activeVendorIds(): Promise<string[]> {
  const { data: vendors } = await supabaseAdmin
    .from("vendors").select("id").eq("status", "active");
  const vendorIds = (vendors ?? []).map((v) => v.id as string);
  if (vendorIds.length === 0) return [];
  const nowIso = new Date().toISOString();
  const { data: subs } = await supabaseAdmin
    .from("subscriptions")
    .select("vendor_id")
    .in("vendor_id", vendorIds)
    .in("status", ["active", "trialing"])
    .or(`current_period_end.is.null,current_period_end.gt.${nowIso}`);
  const subscribed = new Set((subs ?? []).map((s) => s.vendor_id as string));
  return vendorIds.filter((id) => subscribed.has(id));
}

async function vendorHasActiveSubscription(vendorId: string): Promise<boolean> {
  const nowIso = new Date().toISOString();
  const { data } = await supabaseAdmin
    .from("subscriptions")
    .select("id")
    .eq("vendor_id", vendorId)
    .in("status", ["active", "trialing"])
    .or(`current_period_end.is.null,current_period_end.gt.${nowIso}`)
    .limit(1);
  return (data ?? []).length > 0;
}

export const listProducts = createServerFn({ method: "GET" })
  .inputValidator((input: { categorySlug?: string; vendorSlug?: string; vendorId?: string; q?: string; priceMin?: number; priceMax?: number; limit?: number; offset?: number; sort?: "new" | "low" | "high" | "popular" }) =>
    z.object({
      categorySlug: z.string().max(255).optional(),
      vendorSlug: z.string().max(255).optional(),
      vendorId: z.string().uuid().optional(),
      q: z.string().max(200).optional(),
      priceMin: z.number().min(0).max(1000000).optional(),
      priceMax: z.number().min(0).max(1000000).optional(),
      limit: z.number().min(1).max(200).optional(),
      offset: z.number().min(0).max(10000).optional(),
      sort: z.enum(["new", "low", "high", "popular"]).optional(),
    }).parse(input ?? {})
  )
  .handler(async ({ data }) => {
    let query = supabaseAdmin
      .from("products")
      .select("id, slug, title, price, sale_price, featured_image_url, vendor:vendors(id, store_name, slug, status)", { count: "exact" })
      .eq("status", "publish");

    if (data.vendorId) {
      query = query.eq("vendor_id", data.vendorId);
    } else if (data.vendorSlug) {
      const { data: v } = await supabaseAdmin.from("vendors").select("id").eq("slug", data.vendorSlug).maybeSingle();
      if (!v) return { products: [], total: 0 };
      query = query.eq("vendor_id", v.id);
    } else {
      // Only show products from active vendors in the public catalog
      const ids = await activeVendorIds();
      if (ids.length === 0) return { products: [], total: 0 };
      query = query.in("vendor_id", ids);
    }
    if (data.categorySlug) {
      const { data: c } = await supabaseAdmin.from("categories").select("id").eq("slug", data.categorySlug).maybeSingle();
      if (!c) return { products: [], total: 0 };
      // Include the category itself + any direct child (sub)categories so a
      // top-level page automatically lists all of its subcategory products.
      const { data: children } = await supabaseAdmin
        .from("categories").select("id").eq("parent_id", c.id);
      const catIds = [c.id, ...((children ?? []).map((x) => x.id as string))];
      const { data: pcs } = await supabaseAdmin
        .from("product_categories").select("product_id").in("category_id", catIds);
      const ids = Array.from(new Set((pcs ?? []).map((r) => r.product_id as string)));
      if (ids.length === 0) return { products: [], total: 0 };
      query = query.in("id", ids);
    }
    if (data.q && data.q.trim().length > 0) {
      // Strip PostgREST-special chars (`,`, `(`, `)`, `"`, `:`, `*`) plus ilike wildcards (`%`, `_`)
      // to prevent filter-string injection into `.or()`.
      const term = data.q.trim().replace(/[%_(),":*]/g, "");
      if (term.length > 0) {
        const { data: vMatches } = await supabaseAdmin
          .from("vendors")
          .select("id")
          .ilike("store_name", `%${term}%`);
        const vendorIds = (vMatches ?? []).map((v) => v.id as string);
        const orParts = [`title.ilike.%${term}%`, `short_description.ilike.%${term}%`];
        if (vendorIds.length > 0) orParts.push(`vendor_id.in.(${vendorIds.join(",")})`);
        query = query.or(orParts.join(","));
      }
    }
    if (data.priceMin != null) query = query.gte("price", data.priceMin);
    if (data.priceMax != null) query = query.lte("price", data.priceMax);

    // Products without a photo always go last, regardless of the chosen sort.
    query = query.order("has_image", { ascending: false, nullsFirst: false });
    if (data.sort === "low") query = query.order("price", { ascending: true, nullsFirst: false });
    else if (data.sort === "high") query = query.order("price", { ascending: false, nullsFirst: false });
    else if (data.sort === "popular") query = query.order("view_count", { ascending: false, nullsFirst: false });
    else query = query.order("published_at", { ascending: false, nullsFirst: false });

    const limit = data.limit ?? 20;
    const offset = data.offset ?? 0;
    query = query.range(offset, offset + limit - 1);

    try {
      const { data: rows, error, count } = await query;
      if (error) {
        console.error("listProducts query error:", error.message);
        return { products: [], total: 0 };
      }
      return { products: rows ?? [], total: count ?? 0 };
    } catch (e) {
      // Transient network failures to Supabase should not blank the page.
      console.error("listProducts fetch failed:", e instanceof Error ? e.message : e);
      return { products: [], total: 0 };
    }
  });


export const getProduct = createServerFn({ method: "GET" })
  .inputValidator((input: { slug: string }) => z.object({ slug: z.string().min(1).max(255) }).parse(input))
  .handler(async ({ data }) => {
    const { data: product, error } = await supabaseAdmin
      .from("products")
      .select("id, slug, title, description, short_description, price, sale_price, featured_image_url, gallery_urls, stock_quantity, in_stock, vendor:vendors(id, store_name, slug, city, status, shipping_fee, free_shipping_over, description, instagram, youtube, linkedin, facebook, tiktok, profile_id, public_email, public_phone, phone_public)")
      .eq("slug", data.slug)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!product) return { product: null, similar: [] };

    // Hide products from inactive vendors from public view
    const vendorRel = (product as unknown as { vendor: { id: string; status: string } | null }).vendor;
    if (!vendorRel || vendorRel.status !== "active") {
      return { product: null, similar: [] };
    }
    if (!(await vendorHasActiveSubscription(vendorRel.id))) {
      return { product: null, similar: [] };
    }

    // Increment view count (fire-and-forget)
    const currentViews = ((product as unknown as { view_count?: number }).view_count ?? 0);
    supabaseAdmin.from("products").update({ view_count: currentViews + 1 }).eq("id", (product as unknown as { id: string }).id).then(() => {});

    // Map opt-in public contact to vendor.email/phone; never expose auth email
    const vendorObj = (product as unknown as { vendor: Record<string, unknown> }).vendor;
    if (vendorObj) {
      vendorObj.email = vendorObj.public_email ?? null;
      vendorObj.phone = vendorObj.phone_public === true ? (vendorObj.public_phone ?? null) : null;
      delete vendorObj.profile_id;
      delete vendorObj.public_email;
      delete vendorObj.public_phone;
      delete vendorObj.phone_public;
    }

    let similar: Array<{ id: string; slug: string; title: string; price: number | null; featured_image_url: string | null; vendor: { store_name: string; slug: string | null } | null }> = [];
    if (vendorRel?.id) {
      const { data: sim } = await supabaseAdmin
        .from("products")
        .select("id, slug, title, price, sale_price, featured_image_url, vendor:vendors(store_name, slug)")
        .eq("vendor_id", vendorRel.id)
        .eq("status", "publish")
        .neq("id", product.id)
        .order("created_at", { ascending: false })
        .limit(4);
      similar = (sim ?? []) as typeof similar;
    }


    return { product, similar };
  });

export const listVendors = createServerFn({ method: "GET" }).handler(async () => {
  // Only active vendors are listed publicly
  const { data, error } = await supabaseAdmin
    .from("vendors")
    .select("id, slug, store_name, city, description, image_url, status")
    .eq("status", "active")
    .order("store_name", { ascending: true });
  if (error) throw new Error(error.message);

  const subscribed = new Set(await activeVendorIds());
  const vendors = (data ?? []).filter((v) => v.slug && subscribed.has(v.id as string));
  const ids = vendors.map((v) => v.id);
  const counts: Record<string, number> = {};
  if (ids.length) {
    const { data: rows } = await supabaseAdmin
      .from("products")
      .select("vendor_id")
      .in("vendor_id", ids)
      .eq("status", "publish");
    for (const r of rows ?? []) {
      counts[r.vendor_id as string] = (counts[r.vendor_id as string] ?? 0) + 1;
    }
  }
  return { vendors: vendors.map((v) => ({ ...v, product_count: counts[v.id] ?? 0 })) };
});

export const getVendor = createServerFn({ method: "GET" })
  .inputValidator((input: { slug: string }) => z.object({ slug: z.string().min(1).max(255) }).parse(input))
  .handler(async ({ data }) => {
    const { data: vendor, error } = await supabaseAdmin
      .from("vendors")
      .select("id, slug, store_name, city, description, image_url, banner_url, instagram, youtube, linkedin, facebook, tiktok, status, shipping_fee, free_shipping_over, profile_id, public_email, public_phone, phone_public")
      .eq("slug", data.slug)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!vendor || vendor.status !== "active") {
      if (vendor) {
        const authUserId = await getOptionalAuthUserId();
        if (authUserId && (await isOwnerOrAdminOfVendor(authUserId, vendor.profile_id as string | null))) {
          // fall through
        } else {
          return { vendor: null, products: [], categories: [] as Array<{ name: string; slug: string; count: number }>, productCount: 0, priceMin: null as number | null, priceMax: null as number | null };
        }
      } else {
        return { vendor: null, products: [], categories: [] as Array<{ name: string; slug: string; count: number }>, productCount: 0, priceMin: null as number | null, priceMax: null as number | null };
      }
    }
    if (vendor.status === "active" && !(await vendorHasActiveSubscription(vendor.id))) {
      const authUserId = await getOptionalAuthUserId();
      const allowed = authUserId
        ? await isOwnerOrAdminOfVendor(authUserId, vendor.profile_id as string | null)
        : false;
      if (!allowed) return { vendor: null, products: [], categories: [] as Array<{ name: string; slug: string; count: number }>, productCount: 0, priceMin: null as number | null, priceMax: null as number | null };
    }

    const { data: products } = await supabaseAdmin
      .from("products")
      .select("id, slug, title, price, sale_price, featured_image_url, vendor:vendors(store_name, slug)")
      .eq("vendor_id", vendor.id)
      .eq("status", "publish")
      .order("published_at", { ascending: false, nullsFirst: false });

    // Kategorije, v katerih ima prodajalec objavljene izdelke (za SEO povezave)
    const productIds = (products ?? []).map((p) => p.id as string);
    let vendorCategories: Array<{ name: string; slug: string; count: number }> = [];
    if (productIds.length) {
      const { data: pcs } = await supabaseAdmin
        .from("product_categories")
        .select("category_id, product_id")
        .in("product_id", productIds);
      const catCounts = new Map<string, number>();
      for (const r of pcs ?? []) {
        const cid = r.category_id as string;
        catCounts.set(cid, (catCounts.get(cid) ?? 0) + 1);
      }
      if (catCounts.size) {
        const { data: cats } = await supabaseAdmin
          .from("categories")
          .select("id, name, slug")
          .in("id", Array.from(catCounts.keys()));
        vendorCategories = (cats ?? [])
          .map((c) => ({ name: c.name as string, slug: c.slug as string, count: catCounts.get(c.id as string) ?? 0 }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 10);
      }
    }
    const prices = (products ?? [])
      .map((p) => Number((p as { sale_price?: number | null; price?: number | null }).sale_price ?? (p as { price?: number | null }).price ?? 0))
      .filter((n) => Number.isFinite(n) && n > 0);

    // Expose only opt-in public contact; strip internal profile_id and auth email
    const { profile_id: _pid, public_email, public_phone, phone_public, ...rest } = vendor as Record<string, unknown> & { profile_id?: string; public_email?: string | null; public_phone?: string | null; phone_public?: boolean | null };
    return {
      vendor: { ...rest, email: public_email ?? null, phone: phone_public === true ? (public_phone ?? null) : null },
      products: products ?? [],
      categories: vendorCategories,
      productCount: (products ?? []).length,
      priceMin: prices.length ? Math.min(...prices) : null,
      priceMax: prices.length ? Math.max(...prices) : null,
    };
  });


export const listCategories = createServerFn({ method: "GET" }).handler(async () => {
  const { data, error } = await supabaseAdmin
    .from("categories")
    .select("id, name, slug, description")
    .order("name", { ascending: true });
  if (error) throw new Error(error.message);
  const cats = data ?? [];
  // Attach a sample image per category (latest published product with image)
  const { data: pcs } = await supabaseAdmin
    .from("product_categories")
    .select("category_id, product_id");
  const { data: prods } = await supabaseAdmin
    .from("products")
    .select("id, featured_image_url, published_at")
    .eq("status", "publish")
    .not("featured_image_url", "is", null)
    .order("published_at", { ascending: false });
  const catsByProduct = new Map<string, string[]>();
  for (const r of pcs ?? []) {
    const pid = r.product_id as string;
    if (!catsByProduct.has(pid)) catsByProduct.set(pid, []);
    catsByProduct.get(pid)!.push(r.category_id as string);
  }
  const thumbByCat: Record<string, string> = {};
  for (const p of prods ?? []) {
    const ids = catsByProduct.get(p.id as string) ?? [];
    for (const cid of ids) {
      if (!thumbByCat[cid] && p.featured_image_url) thumbByCat[cid] = p.featured_image_url as string;
    }
  }

  return {
    categories: cats.map((c) => ({ ...c, image_url: thumbByCat[c.id] ?? null })),
  };
});


export const listCategoriesWithCounts = createServerFn({ method: "GET" }).handler(async () => {
  const { data: cats, error } = await supabaseAdmin
    .from("categories")
    .select("id, name, slug, description, parent_id, display_order")
    .order("display_order", { ascending: true })
    .order("name", { ascending: true });
  if (error) throw new Error(error.message);
  const activeIds = new Set(await activeVendorIds());
  const { data: pcs } = await supabaseAdmin.from("product_categories").select("category_id, product_id");
  const { data: pubProducts } = await supabaseAdmin
    .from("products").select("id, vendor_id").eq("status", "publish");
  const pubIds = new Set((pubProducts ?? []).filter((p) => activeIds.has(p.vendor_id as string)).map((p) => p.id));
  const ownCounts: Record<string, number> = {};
  for (const r of pcs ?? []) {
    if (pubIds.has(r.product_id as string)) {
      ownCounts[r.category_id as string] = (ownCounts[r.category_id as string] ?? 0) + 1;
    }
  }
  // Roll children counts up into parents
  const totals: Record<string, number> = { ...ownCounts };
  for (const c of cats ?? []) {
    if (c.parent_id && ownCounts[c.id]) {
      totals[c.parent_id as string] = (totals[c.parent_id as string] ?? 0) + ownCounts[c.id];
    }
  }
  return { categories: (cats ?? []).map((c) => ({ ...c, product_count: totals[c.id] ?? 0 })) };
});



export const getCategory = createServerFn({ method: "GET" })
  .inputValidator((input: { slug: string }) => z.object({ slug: z.string().min(1).max(255) }).parse(input))
  .handler(async ({ data }) => {
    const { data: category, error } = await supabaseAdmin
      .from("categories")
      .select("id, name, slug, description, parent_id, parent:parent_id(id, name, slug)")
      .eq("slug", data.slug)
      .maybeSingle();
    if (error) throw new Error(error.message);
    let children: Array<{ id: string; name: string; slug: string }> = [];
    if (category?.id) {
      const { data: kids } = await supabaseAdmin
        .from("categories")
        .select("id, name, slug, display_order")
        .eq("parent_id", category.id)
        .order("display_order", { ascending: true })
        .order("name", { ascending: true });
      children = (kids ?? []).map((k) => ({ id: k.id as string, name: k.name as string, slug: k.slug as string }));
    }
    return { category, children };
  });

export const listFeatured = createServerFn({ method: "GET" })
  .inputValidator((input: { limit?: number; onePerVendor?: boolean }) =>
    z.object({
      limit: z.number().min(1).max(200).optional(),
      onePerVendor: z.boolean().optional(),
    }).parse(input ?? {}),
  )
  .handler(async ({ data }) => {
    // Naključni izdelki vseh prodajalcev z aktivno naročnino.
    const vendorIds = await activeVendorIds();
    if (vendorIds.length === 0) return { products: [] };
    const { data: rows, error } = await supabaseAdmin
      .from("products")
      .select("id, slug, title, price, sale_price, featured_image_url, vendor_id, vendor:vendors(store_name, slug)")
      .eq("status", "publish")
      .in("vendor_id", vendorIds)
      .not("featured_image_url", "is", null)
      .not("price", "is", null)
      .gt("price", 0)
      .limit(2000);
    if (error) throw new Error(error.message);
    // Merilo: izdelek mora imeti naslov, sliko in ceno.
    const all = (rows ?? []).filter((p) => {
      const title = (p as { title?: string | null }).title;
      const image = (p as { featured_image_url?: string | null }).featured_image_url;
      const price = (p as { price?: number | null }).price;
      return Boolean(title && title.trim()) && Boolean(image && image.trim()) && price != null && Number(price) > 0;
    });
    for (let i = all.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [all[i], all[j]] = [all[j], all[i]];
    }
    if (data.onePerVendor) {
      const seen = new Set<string>();
      const picked: typeof all = [];
      for (const p of all) {
        const vid = (p as { vendor_id?: string | null }).vendor_id ?? "";
        if (seen.has(vid)) continue;
        seen.add(vid);
        picked.push(p);
      }
      return { products: picked.slice(0, data.limit ?? 200) };
    }
    return { products: all.slice(0, data.limit ?? 8) };
  });


export const listAllSitemapEntries = createServerFn({ method: "GET" }).handler(async () => {
  const activeIds = await activeVendorIds();
  const [products, vendors, categories] = await Promise.all([
    activeIds.length > 0
      ? supabaseAdmin.from("products").select("slug, updated_at").eq("status", "publish").in("vendor_id", activeIds).limit(5000)
      : Promise.resolve({ data: [] as Array<{ slug: string; updated_at: string | null }> }),
    supabaseAdmin.from("vendors").select("slug, updated_at").eq("status", "active").not("slug", "is", null).limit(2000),
    supabaseAdmin.from("categories").select("slug").limit(500),
  ]);
  return {
    products: (products.data ?? []).map((p) => ({ slug: p.slug as string, updated_at: p.updated_at as string | null })),
    vendors: (vendors.data ?? []).map((v) => ({ slug: v.slug as string, updated_at: v.updated_at as string | null })),
    categories: (categories.data ?? []).map((c) => ({ slug: c.slug as string })),
  };
});
